import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Loader2, ArrowRight, Home, ChevronLeft, ChevronRight, X } from "lucide-react";
import axios_instance from "../api/axios_instance";
import ProductBox from "../components/menus/productBox";
import SideProducts from "../components/menus/sideProducts";
import ScrollablePillBar from "../components/menus/scrollablePillBar";
import { useLanguage } from '../i18n/LanguageContext';

const LIMIT = 12;

const FLAG_FILTERS = [
  { key: 'mostSold',    labelKey: 'filterMostSold', dot: 'bg-orange-500' },
  { key: 'new',         labelKey: 'filterNew',       dot: 'bg-emerald-500' },
  { key: 'featured',    labelKey: 'filterFeatured',  dot: 'bg-amber-500' },
  { key: 'pack',        labelKey: 'filterPack',      dot: 'bg-purple-600' },
  { key: 'promo',       labelKey: 'filterPromo',     dot: 'bg-red-500' },
];

const STATUS_FILTERS = [
  { key: 'available',     labelKey: 'filterAvailable',  dot: 'bg-emerald-500' },
  { key: 'low_stock',     labelKey: 'filterLowStock',   dot: 'bg-orange-400' },
  { key: 'out_of_stock',  labelKey: 'filterOutOfStock', dot: 'bg-red-500' },
];

const INACTIVE = 'bg-white border-gray-200 text-gray-600 hover:border-emerald-500 hover:text-emerald-600';

function ProductsByCategory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, dir, getLabel } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter = searchParams.get('filter') || '';
  const activeStatus = searchParams.get('status') || '';

  const [isOpen, setIsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [activeSort, setActiveSort] = useState(0);

  // Category metadata — resolved from slug, changes only when slug changes
  const [categoryId, setCategoryId] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [parentCategorySlug, setParentCategorySlug] = useState(null);
  const [catLoading, setCatLoading] = useState(true);

  // Products — re-fetched whenever categoryId, filter, or status changes
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const pageRef = useRef(1);
  const sentinelRef = useRef(null);
  const filterRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // Step 1: resolve category from slug — only re-runs when slug changes
  useEffect(() => {
    let cancelled = false;
    setCatLoading(true);
    setCategoryId(null);
    setCurrentCategory(null);
    setSubCategories([]);
    setParentCategorySlug(null);

    axios_instance.get('/api/categories').then(res => {
      if (cancelled) return;
      const found = res.data.find(c => c.slug === slug || c._id === slug);
      if (!found) { setCatLoading(false); return; }

      setCurrentCategory(found);
      setCategoryId(found._id);

      if (found.parentCategory) {
        const parentId = found.parentCategory._id || found.parentCategory;
        const parentObj = res.data.find(c => c._id === parentId);
        setParentCategorySlug(parentObj?.slug || null);
      } else {
        setParentCategorySlug(null);
      }

      const subs = res.data.filter(c => {
        const pid = c.parentCategory?._id || c.parentCategory;
        return pid === found._id;
      });
      setSubCategories(subs);
      setCatLoading(false);
    }).catch(() => setCatLoading(false));

    return () => { cancelled = true; };
  }, [slug]);

  // Step 2: fetch products — re-runs when categoryId, filter, or status changes
  const buildParams = useCallback((p) => {
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (activeFilter) params.set('filter', activeFilter);
    if (activeStatus) params.set('status', activeStatus);
    return params.toString();
  }, [activeFilter, activeStatus]);

  useEffect(() => {
    if (!categoryId) return;
    let cancelled = false;
    setLoading(true);
    setProducts([]);
    pageRef.current = 1;
    setHasMore(true);

    axios_instance.get(`/api/products/category/${categoryId}?${buildParams(1)}`).then(res => {
      if (cancelled) return;
      const list = res.data.products ?? res.data ?? [];
      const pagination = res.data.pagination;
      setProducts(list);
      setTotal(pagination?.total ?? list.length);
      setHasMore(list.length === LIMIT && (!pagination || 1 < pagination.pages));
    }).catch(console.error).finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [categoryId, buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !categoryId) return;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    try {
      const res = await axios_instance.get(`/api/products/category/${categoryId}?${buildParams(nextPage)}`);
      const list = res.data.products ?? res.data ?? [];
      const pagination = res.data.pagination;
      setProducts(prev => [...prev, ...list]);
      pageRef.current = nextPage;
      setHasMore(list.length === LIMIT && (!pagination || nextPage < pagination.pages));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, categoryId, buildParams]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, products.length]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (activeFilter === key) { next.delete('filter'); } else { next.set('filter', key); next.delete('status'); }
    setSearchParams(next);
    setFilterOpen(false);
  };

  const setStatus = (key) => {
    const next = new URLSearchParams(searchParams);
    if (activeStatus === key) { next.delete('status'); } else { next.set('status', key); next.delete('filter'); }
    setSearchParams(next);
    setStatusOpen(false);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('filter');
    next.delete('status');
    setSearchParams(next);
  };

  const activeFilterObj = FLAG_FILTERS.find(f => f.key === activeFilter);
  const activeStatusObj = STATUS_FILTERS.find(s => s.key === activeStatus);
  const hasActiveFilters = !!(activeFilter || activeStatus);

  const [sorted, setSorted] = useState([]);
  const prevLenRef = useRef(0);
  const activeSortRef = useRef(activeSort);
  activeSortRef.current = activeSort;

  const doSort = useCallback((arr) => [...arr].sort((a, b) => {
    const s = activeSortRef.current;
    const aName = getLabel(a.name), bName = getLabel(b.name);
    const aPrice = Math.min(...(a.variants?.map(v => v.price) || [0]));
    const bPrice = Math.min(...(b.variants?.map(v => v.price) || [0]));
    if (s === 0) return aName.localeCompare(bName);
    if (s === 1) return bName.localeCompare(aName);
    if (s === 2) return aPrice - bPrice;
    if (s === 3) return bPrice - aPrice;
    return 0;
  }), [getLabel]);

  useEffect(() => {
    if (products.length === 0) {
      setSorted([]);
      prevLenRef.current = 0;
    } else if (products.length > prevLenRef.current) {
      const newItems = products.slice(prevLenRef.current);
      setSorted(prev => [...prev, ...doSort(newItems)]);
      prevLenRef.current = products.length;
    } else {
      setSorted(doSort(products));
      prevLenRef.current = products.length;
    }
  }, [products, doSort]);

  useEffect(() => {
    setSorted(prev => doSort(prev));
  }, [activeSort]); // eslint-disable-line react-hooks/exhaustive-deps

  if (catLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
    </div>
  );

  const sortOptions = t('productsPage.sortOptions');
  const SepIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  return (
    <div className="bg-white min-h-screen" dir={dir}>

      {/* Header */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <nav className="flex items-center gap-1.5 text-xs font-bold text-gray-400 mb-3">
            <Link to="/" className="flex items-center gap-1.5 hover:text-emerald-600 transition-colors">
              <span className="w-5 h-5 rounded-md bg-emerald-50 flex items-center justify-center">
                <Home className="w-3 h-3 text-emerald-500" />
              </span>
              {t('breadcrumb.home')}
            </Link>
            <SepIcon className="w-3 h-3 text-gray-200" />
            <span>{t('breadcrumb.categories')}</span>
            <SepIcon className="w-3 h-3 text-gray-200" />
            <span className="text-emerald-600 font-black">{getLabel(currentCategory?.name)}</span>
          </nav>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 sm:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-900 tracking-tight leading-tight">
                {getLabel(currentCategory?.name) || t('productsPage.browseProducts')}
              </h1>
              <p className="text-xs font-bold text-gray-400 mt-1">{total} {t('productsPage.inCategory')}</p>
            </div>
            {parentCategorySlug && (
              <button
                onClick={() => navigate(`/products/category/${parentCategorySlug}`)}
                className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-600 font-bold text-xs hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all shrink-0"
              >
                <span className="w-5 h-5 rounded-md bg-white border border-gray-200 flex items-center justify-center">
                  <ArrowRight size={11} className="rotate-180" />
                </span>
                {t('productsPage.backToParent')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Subcategory pills */}
        {subCategories.length > 0 && (
          <div className="mb-4">
            <ScrollablePillBar>
              {subCategories.map((sub) => (
                <button
                  key={sub._id}
                  onClick={() => navigate(`/products/category/${sub.slug}`)}
                  className="whitespace-nowrap px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-bold text-xs hover:border-emerald-600 hover:text-emerald-600 transition-all bg-white"
                >
                  {getLabel(sub.name)}
                </button>
              ))}
            </ScrollablePillBar>
          </div>
        )}

        {/* ── Filter dropdowns ── */}
        <div className="flex items-center gap-2.5 flex-wrap mb-5">

          {/* Flag filter */}
          <div ref={filterRef} className="relative">
            <button
              onClick={() => { setFilterOpen(o => !o); setStatusOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold border transition-all ${activeFilterObj ? 'bg-gray-50 border-gray-300 text-gray-800' : INACTIVE}`}
            >
              {activeFilterObj
                ? <><span className={`w-2 h-2 rounded-full ${activeFilterObj.dot}`} />{t(`productsPage.${activeFilterObj.labelKey}`)}</>
                : t('productsPage.filterType')
              }
              <ChevronDown size={12} className={`transition-transform duration-200 ${filterOpen ? 'rotate-180' : ''}`} />
            </button>

            {filterOpen && (
              <div className={`absolute top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-2xl py-1.5 z-50 overflow-hidden ${dir === 'rtl' ? 'right-0' : 'left-0'}`}>
                {FLAG_FILTERS.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-bold transition-colors text-start ${activeFilter === f.key ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${f.dot}`} />
                    <span className="flex-1">{t(`productsPage.${f.labelKey}`)}</span>
                    {activeFilter === f.key && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status filter */}
          <div ref={statusRef} className="relative">
            <button
              onClick={() => { setStatusOpen(o => !o); setFilterOpen(false); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold border transition-all ${activeStatusObj ? 'bg-gray-50 border-gray-300 text-gray-800' : INACTIVE}`}
            >
              {activeStatusObj
                ? <><span className={`w-2 h-2 rounded-full ${activeStatusObj.dot}`} />{t(`productsPage.${activeStatusObj.labelKey}`)}</>
                : t('productsPage.filterAvailability')
              }
              <ChevronDown size={12} className={`transition-transform duration-200 ${statusOpen ? 'rotate-180' : ''}`} />
            </button>

            {statusOpen && (
              <div className={`absolute top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-2xl py-1.5 z-50 overflow-hidden ${dir === 'rtl' ? 'right-0' : 'left-0'}`}>
                {STATUS_FILTERS.map(s => (
                  <button
                    key={s.key}
                    onClick={() => setStatus(s.key)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-bold transition-colors text-start ${activeStatus === s.key ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${s.dot}`} />
                    <span className="flex-1">{t(`productsPage.${s.labelKey}`)}</span>
                    {activeStatus === s.key && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-gray-400 hover:text-red-500 transition-colors"
            >
              <X size={12} />
              {t('productsPage.clearFilters')}
            </button>
          )}

          {/* Active badge */}
          {(activeFilterObj || activeStatusObj) && (
            <div className="ms-auto flex items-center gap-1.5">
              {activeFilterObj && (
                <span className={`px-3 py-1.5 rounded-lg text-[11px] font-black text-white ${activeFilterObj.dot}`}>
                  {t(`productsPage.${activeFilterObj.labelKey}`)}
                </span>
              )}
              {activeStatusObj && (
                <span className={`px-3 py-1.5 rounded-lg text-[11px] font-black text-white ${activeStatusObj.dot}`}>
                  {t(`productsPage.${activeStatusObj.labelKey}`)}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <main className="flex-1">

            {/* Sort + count */}
            <div className="flex items-center justify-between gap-3 mb-6 pb-5 border-b border-gray-100">
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-3 bg-white border border-gray-200 px-4 py-2 rounded-xl text-xs font-bold hover:border-emerald-600 transition-all min-w-[160px]"
                >
                  <span className="flex-1 text-start">{sortOptions[activeSort]}</span>
                  <span className={`w-5 h-5 rounded-md bg-gray-100 flex items-center justify-center transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 bg-emerald-100' : ''}`}>
                    <ChevronDown size={11} className={isOpen ? 'text-emerald-600' : 'text-gray-500'} />
                  </span>
                </button>
                {isOpen && (
                  <div className="absolute top-full start-0 mt-1 w-full bg-white border border-gray-200 rounded-xl z-50 overflow-hidden py-1">
                    {sortOptions.map((option, i) => (
                      <button
                        key={i}
                        onClick={() => { setActiveSort(i); setIsOpen(false); }}
                        className={`w-full text-start px-4 py-2.5 text-xs font-bold transition-colors ${activeSort === i ? 'text-emerald-600 bg-emerald-50' : 'hover:bg-gray-50'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-xs font-bold text-gray-400">
                {products.length}{hasMore ? '+' : ''} / {total} {t('productsPage.available')}
              </span>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : sorted.length > 0 ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                {sorted.map((product, i) => (
                  <ProductBox key={product._id} product={product} index={i} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24 rounded-3xl border-2 border-dashed border-gray-200">
                <p className="text-gray-400 font-bold text-sm">{t('productsPage.noCategoryProducts')}</p>
              </div>
            )}

            <div ref={sentinelRef} className="flex justify-center py-8">
              {loadingMore && <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />}
              {!hasMore && products.length > 0 && (
                <p className="text-xs font-bold text-gray-300">{t('productsPage.allLoaded')}</p>
              )}
            </div>
          </main>

          <aside className="w-full lg:w-[280px] xl:w-[300px] shrink-0">
            <div className="lg:sticky lg:top-28 border border-gray-100 rounded-3xl p-6 bg-white">
              <SideProducts name={t('productsPage.mostRequested')} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default ProductsByCategory;
